import { Application } from "pixi.js";

import { Scene } from "./scenes/Scene";

/** Colour behind everything — only visible for the frame or two before the sea exists. */
const CLEAR_COLOR = 0x0b2a35;
/** Longest step handed to a scene, in ms. Clamps the catch-up spike after a stalled tab. */
const MAX_FRAME_MS = 100;

/**
 * Owns the Pixi application and the active scene. Static because there is exactly one
 * game; scenes reach it through `Game.app` / `Game.width` / `Game.height`.
 */
export class Game {
    private static _app: Application;
    private static _scene: Scene | null = null;

    public static get app(): Application {
        return this._app;
    }

    public static get width(): number {
        return this._app.screen.width;
    }

    public static get height(): number {
        return this._app.screen.height;
    }

    public static async boot(): Promise<void> {
        this._app = new Application();

        await this._app.init({
            resizeTo: window,
            background: CLEAR_COLOR,
            antialias: true,
            resolution: window.devicePixelRatio,
            autoDensity: true
        });

        document.body.appendChild(this._app.canvas);

        // Scenes tune in per-second units, and deltaMS is capped so a backgrounded tab
        // does not resume with one enormous step.
        this._app.ticker.add((ticker) => this._scene?.update(Math.min(ticker.deltaMS, MAX_FRAME_MS) / 1000));
        this._app.renderer.on("resize", (width: number, height: number) => this._scene?.resize(width, height));
    }

    public static async changeScene(scene: Scene): Promise<void> {
        if (this._scene) {
            this._app.stage.removeChild(this._scene.container);
            this._scene.destroy();
        }

        this._scene = scene;
        this._app.stage.addChild(scene.container);

        await scene.enter(this.width, this.height);
    }
}
